import {

  createContext,

  useCallback,

  useContext,

  useEffect,

  useMemo,

  useRef,

  useState,

} from "react";

import { useLocation } from "react-router-dom";

import { useSelector } from "react-redux";

import { createTrainingStoreController } from "./trainingStore";

import { patchClinicApiForTraining } from "./trainingApiPatch";

import {

  buildTrainingLessons,

  loadTrainingProgress,

  saveTrainingProgress,

} from "./trainingLessons";

import { getTrainingLessonSubsteps, getTrainingNavOpts, getTrainingSubsteps } from "./trainingGuide";

import { clearAwaitingGuide } from "../components/Clinic/AdminOnboarding/onboardingStorage";
import { useIsMobile } from "../hooks/useMediaQuery";
import clinicAPI from "../services/clinicApi";
import { isQuickStartFeatureOn } from "../utils/productFlags";
import {
  loadActivationMeta,
  matchesActivationRoute,
  isMoreMenuNavSubstep,
  navIdFromTourTarget,
  normalizeTourSubstepId,
  QUICK_START_ACCEPTED_EVENT,
  QUICK_START_WELCOME_EVENT,
  saveActivationMeta,
  setupToActivationFlags,
} from "../activation/activationPath";
import { isCoreBottomTab } from "../utils/adminNav";

function trainingNavOpts(isMobile, track) {
  return getTrainingNavOpts(isMobile, track);
}

function shouldAutoNavigateToLesson(lesson, firstSubstep) {
  if (!lesson?.route) return false;
  if (firstSubstep?.kind === "nav") return false;
  return true;
}

function lessonRouteNeedsNavigation(location, route) {
  if (!route) return false;
  return !matchesActivationRoute(
    location.pathname,
    location.search,
    route,
  );
}



const TrainingModeContext = createContext(null);



function firstIncomplete(lessons, completed, skipped) {

  const done = new Set([...(completed || []), ...(skipped || [])]);

  return lessons.find((l) => !done.has(l.id)) || null;

}



export function TrainingModeProvider({ children }) {
  if (!isQuickStartFeatureOn()) {
    return children;
  }
  return <TrainingModeProviderEnabled>{children}</TrainingModeProviderEnabled>;
}

function TrainingModeProviderEnabled({ children }) {

  const tenantId = useSelector((state) => state.auth?.tenant?.id);

  const isMobile = useIsMobile(768);

  const location = useLocation();

  const storeRef = useRef(null);

  const restoreRef = useRef(null);

  const [active, setActive] = useState(false);
  const [realData, setRealData] = useState(false);
  const [replayMode, setReplayMode] = useState(false);
  const [lessonScope, setLessonScope] = useState(null);
  const [track, setTrack] = useState("quick");

  const navOpts = useMemo(
    () => trainingNavOpts(isMobile, track),
    [isMobile, track],
  );

  const [lessonId, setLessonId] = useState("direction");

  const [completed, setCompleted] = useState([]);

  const [skipped, setSkipped] = useState([]);

  const [feedback, setFeedback] = useState(null);

  const [offerOpen, setOfferOpen] = useState(false);

  const [coachCollapsed, setCoachCollapsedState] = useState(true);

  const setCoachCollapsed = useCallback(
    (value) => {
      if (isMobile) {
        setCoachCollapsedState(true);
        return;
      }
      const next = typeof value === "function" ? value(coachCollapsed) : value;
      setCoachCollapsedState(Boolean(next));
    },
    [coachCollapsed, isMobile],
  );

  const [trainingSubstepByLesson, setTrainingSubstepByLesson] = useState({});

  const [pendingLessonRoute, setPendingLessonRoute] = useState(null);

  const [tick, setTick] = useState(0);

  const completedRef = useRef([]);

  const skippedRef = useRef([]);



  const lessons = useMemo(
    () => buildTrainingLessons(track, { scope: lessonScope || undefined }),
    [track, lessonScope],
  );



  const lessonById = useCallback(

    (id) => lessons.find((l) => l.id === id) || lessons[0] || null,

    [lessons],

  );



  useEffect(() => {

    completedRef.current = completed;

  }, [completed]);

  useEffect(() => {

    skippedRef.current = skipped;

  }, [skipped]);



  const persist = useCallback(

    (patch) => {

      if (!tenantId) return;

      saveTrainingProgress(tenantId, patch);

    },

    [tenantId],

  );



  const showFeedback = useCallback((message, tone = "success") => {

    setFeedback({ message, tone, at: Date.now() });

    window.setTimeout(() => setFeedback(null), 2800);

  }, []);



  const stopTraining = useCallback(() => {

    if (restoreRef.current) {

      restoreRef.current();

      restoreRef.current = null;

    }

    storeRef.current = null;
    setActive(false);
    setRealData(false);
    setLessonScope(null);
    setReplayMode(false);
    setFeedback(null);

    setCoachCollapsed(false);

  }, []);



  const startTraining = useCallback(
    (opts = {}) => {
      if (restoreRef.current) {
        restoreRef.current();
        restoreRef.current = null;
      }

      const useReal = Boolean(opts.realData);
      const progress = loadTrainingProgress(tenantId);
      const nextTrack = opts.track || progress.track || "quick";
      const nextScope =
        opts.scope !== undefined
          ? opts.scope
          : useReal && nextTrack === "full"
            ? "full"
            : null;
      const nextReplay = Boolean(opts.replayMode ?? opts.reset);
      setRealData(useReal);
      setLessonScope(nextScope);
      setReplayMode(nextReplay);

      if (useReal) {
        storeRef.current = null;
      } else {
        const store = createTrainingStoreController();
        storeRef.current = store;
        restoreRef.current = patchClinicApiForTraining(store);
      }

      const catalog = buildTrainingLessons(nextTrack, {
        scope: nextScope || undefined,
        lessonIds: opts.lessonIds,
      });

      const completedIds = opts.reset
        ? []
        : Array.isArray(progress.completed)
          ? progress.completed
          : [];
      const skippedIds = opts.reset
        ? []
        : Array.isArray(progress.skipped)
          ? progress.skipped
          : [];

      const nextLesson =
        firstIncomplete(catalog, completedIds, skippedIds) || catalog[0];

      setTrack(nextTrack);
      setCompleted(completedIds);
      completedRef.current = completedIds;
      setSkipped(skippedIds);
      skippedRef.current = skippedIds;
      setLessonId(nextLesson?.id || catalog[0]?.id || "direction");
      setActive(true);
      setOfferOpen(false);
      setCoachCollapsed(true);

      const startNavOpts = trainingNavOpts(isMobile, nextTrack);
      const subs = getTrainingLessonSubsteps(nextLesson?.id, startNavOpts);
      if (subs[0]) {
        setTrainingSubstepByLesson({ [nextLesson.id]: subs[0].id });
      }
      setPendingLessonRoute(
        shouldAutoNavigateToLesson(nextLesson, subs[0]) &&
          lessonRouteNeedsNavigation(location, nextLesson.route)
          ? nextLesson.route
          : null,
      );

      persist({
        completed: completedIds,
        skipped: skippedIds,
        offerDismissed: true,
        track: nextTrack,
        realData: useReal,
      });

      if (tenantId) {
        clearAwaitingGuide(tenantId);
        if (useReal && opts.reset) {
          saveActivationMeta(tenantId, {
            tourVisits: {},
            fullTrainingBannerDismissed: false,
          });
        }
      }

      showFeedback(
        useReal
          ? nextTrack === "quick"
            ? "Быстрый старт — 8 шагов. Сначала откройте раздел в меню."
            : nextReplay
              ? "Обучение начато — пройдите все шаги с подсказками"
              : "Полное обучение — подсказки на реальных данных клуба"
          : "Режим обучения включён — данные только в браузере",
        "info",
      );
    },
    [isMobile, location.pathname, location.search, persist, showFeedback, tenantId],
  );



  const exitTraining = useCallback(
    (opts = {}) => {
      const wasReal = realData;
      stopTraining();
      if (opts.message !== false) {
        showFeedback(
          opts.message ||
            (wasReal
              ? "Обучение завершено"
              : "Вы вышли из обучения. Данные клуба не менялись."),
          "info",
        );
      }
    },
    [realData, showFeedback, stopTraining],
  );



  useEffect(

    () => () => {

      if (restoreRef.current) restoreRef.current();

    },

    [],

  );



  const focusLesson = useCallback(
    (id, { announce = false } = {}) => {
      if (!lessons.some((l) => l.id === id)) return null;
      const lesson = lessonById(id);
      const subs = getTrainingLessonSubsteps(id, navOpts);
      const first = subs[0] || null;
      setLessonId(id);
      if (first) {
        setTrainingSubstepByLesson((prev) => ({ ...prev, [id]: first.id }));
      }
      setPendingLessonRoute(
        shouldAutoNavigateToLesson(lesson, first) &&
          lessonRouteNeedsNavigation(location, lesson.route)
          ? lesson.route
          : null,
      );
      setCoachCollapsed(true);
      if (announce && lesson) {
        const lead =
          first?.kind === "nav"
            ? `${lesson.title} — откройте раздел в меню`
            : lesson.title;
        showFeedback(`Шаг ${lesson.index}: ${lead}`, "info");
      }
      return lesson;
    },
    [lessonById, lessons, location.pathname, location.search, navOpts, showFeedback],
  );

  const completeLesson = useCallback(
    (id, { silent, autoAdvance = true } = {}) => {
      const alreadyDone = completedRef.current.includes(id);
      const lesson = lessonById(id);

      if (!alreadyDone) {
        const nextCompleted = [...completedRef.current, id];
        completedRef.current = nextCompleted;
        setCompleted(nextCompleted);
        persist({ completed: nextCompleted });

        if (skippedRef.current.includes(id)) {
          const nextSkipped = skippedRef.current.filter((x) => x !== id);
          skippedRef.current = nextSkipped;
          setSkipped(nextSkipped);
          persist({ skipped: nextSkipped });
        }

        if (!silent) {
          showFeedback(lesson?.success || "Правильно!", "success");
        }
        setTick((t) => t + 1);
      }

      if (autoAdvance) {
        const next = firstIncomplete(
          lessons,
          completedRef.current,
          skippedRef.current,
        );
        if (next) {
          focusLesson(next.id, { announce: !silent && !alreadyDone });
        }
      }

      const remaining = lessons.filter(
        (l) =>
          !completedRef.current.includes(l.id) &&
          !skippedRef.current.includes(l.id),
      );
      return remaining.length === 0;
    },
    [focusLesson, lessonById, lessons, persist, showFeedback],
  );



  const clearPendingLessonRoute = useCallback(() => {

    setPendingLessonRoute(null);

  }, []);



  const skipLesson = useCallback(
    (id) => {
      const nextSkipped = skippedRef.current.includes(id)
        ? skippedRef.current
        : [...skippedRef.current, id];
      skippedRef.current = nextSkipped;
      setSkipped(nextSkipped);
      persist({ skipped: nextSkipped });

      const next = firstIncomplete(
        lessons,
        completedRef.current,
        nextSkipped,
      );
      if (!next) {
        showFeedback("Курс пройден (часть шагов пропущена)", "info");
        return null;
      }

      focusLesson(next.id, { announce: true });
      return next;
    },
    [focusLesson, lessons, persist, showFeedback],
  );

  const goNextLesson = useCallback(() => {
    const next = firstIncomplete(
      lessons,
      completedRef.current,
      skippedRef.current,
    );

    if (!next) {
      showFeedback(
        "Готово — теперь то же в своём клубе. Добавьте настоящего ученика.",
        "success",
      );
      return null;
    }

    focusLesson(next.id, { announce: true });
    return next;
  }, [focusLesson, lessons, showFeedback]);



  const selectLesson = focusLesson;



  const trainingSubsteps = useMemo(

    () => getTrainingLessonSubsteps(lessonId, navOpts),

    [lessonId, navOpts],

  );



  const rawTrainingSubstepId =
    trainingSubsteps.length > 0
      ? trainingSubstepByLesson[lessonId] || trainingSubsteps[0]?.id
      : null;

  const trainingSubstepId = normalizeTourSubstepId(
    lessonId,
    rawTrainingSubstepId,
  );

  useEffect(() => {
    if (!lessonId || !rawTrainingSubstepId) return;
    const normalized = normalizeTourSubstepId(lessonId, rawTrainingSubstepId);
    if (normalized !== rawTrainingSubstepId) {
      setTrainingSubstepByLesson((prev) => ({ ...prev, [lessonId]: normalized }));
    }
  }, [lessonId, rawTrainingSubstepId]);

  const currentTrainingSubstep = useMemo(

    () => trainingSubsteps.find((s) => s.id === trainingSubstepId) || null,

    [trainingSubsteps, trainingSubstepId],

  );



  const setTrainingSubstep = useCallback((lesson, subId) => {

    setTrainingSubstepByLesson((prev) => ({ ...prev, [lesson]: subId }));

  }, []);



  const advanceTrainingSubstep = useCallback(() => {

    const subs = getTrainingLessonSubsteps(lessonId, navOpts);

    if (!subs.length) return null;

    const cur = trainingSubstepByLesson[lessonId] || subs[0]?.id;

    const idx = subs.findIndex((s) => s.id === cur);

    const next = subs[idx + 1];

    if (next) {

      setTrainingSubstep(lessonId, next.id);

      if (next.kind !== "nav") {

        const lesson = lessonById(lessonId);

        if (
          lesson?.route &&
          lessonRouteNeedsNavigation(location, lesson.route)
        ) {
          setPendingLessonRoute(lesson.route);
        }

      }

      return next;

    }

    completeLesson(lessonId);
    return null;

  }, [
    completeLesson,
    lessonId,
    lessonById,
    location.pathname,
    location.search,
    navOpts,
    setTrainingSubstep,
    trainingSubstepByLesson,
  ]);



  useEffect(() => {

    if (!active) return;

    const subs = getTrainingLessonSubsteps(lessonId, navOpts);

    if (!subs.length) return;

    const cur = trainingSubstepByLesson[lessonId];
    const curSub = subs.find((s) => s.id === cur) || null;
    const lesson = lessonById(lessonId);

    if (
      curSub &&
      curSub.kind !== "nav" &&
      subs[0]?.kind === "nav" &&
      lesson?.route &&
      !matchesActivationRoute(
        location.pathname,
        location.search,
        lesson.route,
      )
    ) {
      setTrainingSubstep(lessonId, subs[0].id);
      setPendingLessonRoute(null);
      return;
    }

    if (!cur || !subs.some((s) => s.id === cur)) {

      setTrainingSubstep(lessonId, subs[0].id);

    }

  }, [
    active,
    lessonId,
    lessonById,
    location.pathname,
    location.search,
    navOpts,
    setTrainingSubstep,
    trainingSubstepByLesson,
  ]);



  useEffect(() => {
    if (!active || !currentTrainingSubstep || currentTrainingSubstep.kind !== "nav") {
      return;
    }

    const path = location.pathname.replace(/\/$/, "");
    const lesson = lessonById(lessonId);
    const onLessonRoute =
      lesson?.route &&
      matchesActivationRoute(location.pathname, location.search, lesson.route);

    if (currentTrainingSubstep.id.endsWith("_nav_hub")) {
      if (path === "/manage/app/more") {
        advanceTrainingSubstep();
      }
      return;
    }

    if (lesson && onLessonRoute && isMoreMenuNavSubstep(currentTrainingSubstep.id)) {
      advanceTrainingSubstep();
      return;
    }

    if (
      isMobile &&
      lesson &&
      isMoreMenuNavSubstep(currentTrainingSubstep.id) &&
      !onLessonRoute
    ) {
      const navId = navIdFromTourTarget(currentTrainingSubstep.target);
      if (navId && !isCoreBottomTab(navId) && path !== "/manage/app/more") {
        setTrainingSubstep(lesson.id, `${lesson.id}_nav_hub`);
      }
      return;
    }

    if (
      currentTrainingSubstep.id.endsWith("_nav") &&
      !currentTrainingSubstep.id.endsWith("_nav_hub") &&
      onLessonRoute
    ) {
      advanceTrainingSubstep();
    }
  }, [
    active,
    lessonId,
    lessonById,
    isMobile,
    currentTrainingSubstep,
    location.pathname,
    location.search,
    advanceTrainingSubstep,
    setTrainingSubstep,
  ]);

  useEffect(() => {
    if (!active || realData || !storeRef.current) return undefined;
    const store = storeRef.current;
    const check = () => {
      const lesson = lessonById(lessonId);
      if (!lesson) return;
      const pred = store.predicates[lesson.predicate];
      if (typeof pred === "function" && pred()) {
        if (
          !completedRef.current.includes(lesson.id) &&
          !skippedRef.current.includes(lesson.id)
        ) {
          completeLesson(lesson.id);
        }
      }
      setTick((t) => t + 1);
    };
    check();
    return store.subscribe(() => check());
  }, [active, lessonId, completeLesson, lessonById, realData]);

  useEffect(() => {
    if (!active || !realData || !tenantId || replayMode) return undefined;
    let alive = true;
    const check = async () => {
      try {
        const status = await clinicAPI.getClinicSetupStatus();
        if (!alive) return;
        const flags = setupToActivationFlags(status);
        const meta = loadActivationMeta(tenantId);
        const merged = {
          ...flags,
          hasParentInvite: Boolean(meta.parentInviteSeen),
        };
        for (const lesson of lessons) {
          if (completedRef.current.includes(lesson.id)) continue;
          if (skippedRef.current.includes(lesson.id)) continue;
          let done = false;
          if (lesson.trainingOnly) {
            done = Boolean(meta.tourVisits?.[lesson.id]);
          } else if (lesson.predicate) {
            done = Boolean(merged[lesson.predicate]);
          }
          if (done) completeLesson(lesson.id, { silent: true });
        }
      } catch {
        /* ignore */
      }
    };
    check();
    const id = window.setInterval(check, 4000);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, [active, completeLesson, lessons, realData, replayMode, tenantId]);

  const markTourVisit = useCallback(
    (id) => {
      if (realData && tenantId) {
        const meta = loadActivationMeta(tenantId);
        saveActivationMeta(tenantId, {
          tourVisits: { ...(meta.tourVisits || {}), [id]: true },
        });
        setTick((t) => t + 1);
        return;
      }
      storeRef.current?.actions.markTourVisit(id);
    },
    [realData, tenantId],
  );

  const dismissFullTrainingBanner = useCallback(() => {
    if (!tenantId) return;
    saveActivationMeta(tenantId, { fullTrainingBannerDismissed: true });
  }, [tenantId]);

  const startFullTraining = useCallback(
    (opts = {}) => {
      startTraining({
        realData: true,
        track: "full",
        scope: opts.scope === "extended" ? "extended" : "full",
        reset: Boolean(opts.reset ?? true),
        replayMode: true,
      });
      if (tenantId) {
        saveActivationMeta(tenantId, { fullTrainingBannerDismissed: true });
      }
    },
    [startTraining, tenantId],
  );

  const startExtendedTraining = useCallback(
    (opts = {}) => startFullTraining({ ...opts, scope: "extended" }),
    [startFullTraining],
  );

  const startQuickTraining = useCallback(
    (opts = {}) => {
      if (tenantId) {
        const meta = loadActivationMeta(tenantId);
        if (!meta.welcomeSeen) {
          window.dispatchEvent(
            new CustomEvent(QUICK_START_WELCOME_EVENT, { detail: opts }),
          );
          return;
        }
        saveActivationMeta(tenantId, {
          dismissed: false,
          skipped: [],
          substepByStep: {},
          collapsed: true,
        });
      }
      startTraining({
        realData: true,
        track: "quick",
        reset: Boolean(opts.reset ?? true),
        replayMode: true,
      });
    },
    [startTraining, tenantId],
  );

  useEffect(() => {
    const onAccepted = (event) => {
      const opts = event.detail || {};
      if (tenantId) {
        saveActivationMeta(tenantId, {
          dismissed: false,
          skipped: [],
          substepByStep: {},
          collapsed: true,
        });
      }
      startTraining({
        realData: true,
        track: "quick",
        reset: Boolean(opts.reset ?? true),
        replayMode: true,
      });
    };
    window.addEventListener(QUICK_START_ACCEPTED_EVENT, onAccepted);
    return () =>
      window.removeEventListener(QUICK_START_ACCEPTED_EVENT, onAccepted);
  }, [startTraining, tenantId]);



  const openOffer = useCallback(() => setOfferOpen(true), []);

  const dismissOffer = useCallback(() => {

    setOfferOpen(false);

    persist({ offerDismissed: true });

    if (tenantId) clearAwaitingGuide(tenantId);

  }, [persist, tenantId]);



  const currentLesson = lessonById(lessonId);

  const progressCount = completed.filter((id) =>

    lessons.some((l) => l.id === id),

  ).length;

  const allDone = lessons.every(

    (l) => completed.includes(l.id) || skipped.includes(l.id),

  );

  const onLessonRoute = useMemo(() => {
    if (!currentLesson?.route) return false;
    return matchesActivationRoute(
      location.pathname,
      location.search,
      currentLesson.route,
    );
  }, [currentLesson, location.pathname, location.search]);

  const tourSurfaceActive = useMemo(() => {
    if (!active || allDone || !currentLesson) return false;
    if (
      completed.includes(currentLesson.id) ||
      skipped.includes(currentLesson.id)
    ) {
      return false;
    }
    return (
      onLessonRoute &&
      Boolean(currentTrainingSubstep) &&
      currentTrainingSubstep.kind !== "nav"
    );
  }, [
    active,
    allDone,
    completed,
    currentLesson,
    currentTrainingSubstep,
    onLessonRoute,
    skipped,
  ]);

  useEffect(() => {
    if (tourSurfaceActive) setCoachCollapsed(true);
  }, [tourSurfaceActive, lessonId, trainingSubstepId]);



  const value = useMemo(

    () => ({

      active,
      realData,
      replayMode,
      track,

      lessonId,

      currentLesson,

      lessons,

      completed,

      skipped,

      progressCount,

      totalLessons: lessons.length,

      tourSurfaceActive,

      allDone,

      feedback,

      offerOpen,

      coachCollapsed,

      setCoachCollapsed,

      onLessonRoute,

      trainingSubstepId,

      trainingSubsteps,

      currentTrainingSubstep,

      setTrainingSubstep,

      advanceTrainingSubstep,

      pendingLessonRoute,

      clearPendingLessonRoute,

      tick,

      store: storeRef.current,

      startTraining,

      exitTraining,

      stopTraining,

      completeLesson,

      skipLesson,

      goNextLesson,

      selectLesson,

      openOffer,

      dismissOffer,

      setOfferOpen,

      showFeedback,

      markLinkCopied: () => {
        if (realData && tenantId) {
          saveActivationMeta(tenantId, { parentInviteSeen: true });
          setTick((t) => t + 1);
        } else {
          storeRef.current?.actions.markLinkCopied();
        }
        if (active && lessonId === "parents") {
          completeLesson("parents");
        }
      },
      markInviteSeen: () => storeRef.current?.actions.markInviteSeen(),
      markTourVisit,
      dismissFullTrainingBanner,
      startFullTraining,
      startExtendedTraining,
      startQuickTraining,

    }),

    [

      active,

      allDone,

      coachCollapsed,

      completeLesson,

      completed,

      currentLesson,

      dismissFullTrainingBanner,

      dismissOffer,

      exitTraining,

      feedback,

      goNextLesson,

      lessonId,

      lessons,

      location.pathname,

      location.search,

      markTourVisit,

      offerOpen,

      openOffer,

      onLessonRoute,

      progressCount,

      realData,

      replayMode,

      selectLesson,

      showFeedback,

      skipLesson,

      startFullTraining,
      startExtendedTraining,
      startQuickTraining,

      startTraining,

      stopTraining,

      skipped,

      tick,

      tourSurfaceActive,

      track,

      trainingSubstepId,

      trainingSubsteps,

      currentTrainingSubstep,

      advanceTrainingSubstep,

      setTrainingSubstep,

      pendingLessonRoute,

      clearPendingLessonRoute,

    ],

  );



  return (

    <TrainingModeContext.Provider value={value}>

      {children}

    </TrainingModeContext.Provider>

  );

}



export function useTrainingMode() {

  const ctx = useContext(TrainingModeContext);

  if (!ctx) {

    return {

      active: false,
      startTraining: () => {},
      startFullTraining: () => {},
      startExtendedTraining: () => {},
      exitTraining: () => {},
      openOffer: () => {},
      offerOpen: false,
      lessons: buildTrainingLessons("quick"),
      track: "quick",
    };

  }

  return ctx;

}


